import { useQuery } from '@tanstack/react-query';
import { listEvents, getEventDetail } from '../../api/eventsApi';
import type { EventListItem } from '../../api/types';
import { filtersToEventQuery, type AgentHistoryFilters } from './agentHistory.types';

// 권위: docs/ARCH_DASHBOARD.md §4.4 업데이트 주기 — "실시간 불필요, 30초 자동 갱신 옵션".
// 화면 4 는 과거 조회 중심이므로 WebSocket prepend 는 하지 않는다 (= 실시간 배지·노이즈 없음).
// 실시간 관찰은 화면 5 Live Monitor 가 담당.

const QK_LIST = 'agent-history-list';
const QK_DETAIL = 'agent-history-detail';
const QK_TRACE = 'agent-history-trace';

export function useAgentHistoryList(filters: AgentHistoryFilters) {
  const query = useQuery({
    queryKey: [QK_LIST, filters],
    queryFn: () => listEvents(filtersToEventQuery(filters)),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  // trace_id / run_id 는 백엔드 필터 미지원 → 클라이언트에서 필터링.
  const serverItems = query.data?.items ?? [];
  const items = applyClientFilters(serverItems, filters);

  return {
    data: query.data
      ? {
          items,
          total: query.data.total,
          unackedCount: query.data.unackedCount,
          // 필터링으로 클라이언트에서 제거된 건수 — UI 알림용.
          filteredOutCount: serverItems.length - items.length,
        }
      : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    error: (query.error as Error | null) ?? null,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

export function useAgentHistoryDetail(eventId: string | null) {
  return useQuery({
    queryKey: [QK_DETAIL, eventId],
    queryFn: () => getEventDetail(eventId as string),
    enabled: eventId != null,
    staleTime: 5_000,
  });
}

/**
 * 동일 trace_id 로 묶인 최근 이벤트 체인을 가져온다.
 * 백엔드가 trace_id 필터를 지원하지 않으므로, 최근 200건을 받아서 클라이언트에서 필터한다.
 * (운영상 한 trace_id 가 만들어내는 이벤트 수는 보통 < 50.)
 */
export function useTraceChain(traceId: string | null) {
  return useQuery({
    queryKey: [QK_TRACE, traceId],
    queryFn: async () => {
      const resp = await listEvents({ pageSize: 200, page: 0 });
      return resp.items.filter((it) => it.traceId === traceId);
    },
    enabled: !!traceId,
    staleTime: 30_000,
  });
}

function applyClientFilters(items: EventListItem[], f: AgentHistoryFilters): EventListItem[] {
  const traceId = f.traceId.trim();
  const runId = f.runId.trim();
  if (!traceId && !runId) return items;
  return items.filter((it) => {
    if (traceId && it.traceId !== traceId) return false;
    if (runId) {
      const refs = it.entityRefs ?? {};
      const refRunId =
        ((refs as Record<string, unknown>).run_id as string | undefined) ??
        ((refs as Record<string, unknown>).runId as string | undefined);
      if (refRunId !== runId) {
        // fallback: summary 에 run_id 문자열 포함 — Python 에이전트가 refs 에 담지 않는 경우 대비.
        if (!it.summary.includes(runId)) return false;
      }
    }
    return true;
  });
}
